import { apiClient } from './api';
import type {
  PhaseDetectionRequest,
  PhaseDetectionResponse,
  MovementTemplate,
  CreateTemplateRequest,
  CreateTemplateResponse,
  Phase,
} from '@/types/api';

/**
 * Response shape of `POST /phases/detect?video_id=...` (the query-param /
 * "simplified" variant hit by `detectPhasesFromVideo` below) — distinct from
 * `PhaseDetectionResponse`, which is the body-request variant's shape
 * (`detectPhases` above). Field names differ (`detected_phases` vs `phases`,
 * plus `confidence_scores` and the echoed trim window) — see
 * `PhaseDetectionPreview.tsx`, the sole caller, for the accessed fields.
 */
export interface PhaseDetectionFromVideoResponse {
  detected_phases?: Phase[];
  confidence_scores?: number[];
  trim_start_frame?: number;
  trim_end_frame?: number;
}

/**
 * Raw template shape as returned by the backend's `/phases/templates*`
 * endpoints, before `transformTemplateResponse()` normalizes it into
 * `MovementTemplate`. The backend uses different field names on this
 * particular family of routes (`template_id`/`video_name`/`movement_name`)
 * than the rest of the `MovementTemplate` fields, which otherwise pass
 * through unchanged (hence `Partial<MovementTemplate>`).
 */
interface RawTemplateResponse extends Partial<MovementTemplate> {
  template_id?: string;
  video_name?: string;
  movement_name?: string;
  auto_provisioned_stance_gate?: AutoProvisionedStanceGate | null;
}

/**
 * Detect phases in a video using full request body
 */
export async function detectPhases(
  request: PhaseDetectionRequest
): Promise<PhaseDetectionResponse> {
  const response = await apiClient.post<PhaseDetectionResponse>(
    '/phases/detect',
    request
  );
  return response.data;
}

/**
 * Detect phases in a video using query parameters (simplified).
 *
 * Optional trim params (B-2026-0102 Phase 1):
 *   trim_start_frame — inclusive first frame to analyse (default 0).
 *   trim_end_frame   — inclusive last frame to analyse (omit = end of video).
 *
 * Phase boundaries in the response are relative to the trimmed clip
 * (frame 0 = trim_start_frame), which matches how the wizard already
 * treats phases after a coach-defined trim.
 */
export async function detectPhasesFromVideo(
  videoId: string,
  options?: {
    method?: string;
    min_phase_duration?: number;
    trim_start_frame?: number;
    trim_end_frame?: number;
  }
): Promise<PhaseDetectionFromVideoResponse> {
  const params = new URLSearchParams({ video_id: videoId });
  if (options?.method) params.set('method', options.method);
  if (options?.min_phase_duration !== undefined) {
    params.set('min_phase_duration', options.min_phase_duration.toString());
  }
  if (options?.trim_start_frame !== undefined) {
    params.set('trim_start_frame', options.trim_start_frame.toString());
  }
  if (options?.trim_end_frame !== undefined) {
    params.set('trim_end_frame', options.trim_end_frame.toString());
  }

  const response = await apiClient.post<PhaseDetectionFromVideoResponse>(
    `/phases/detect?${params.toString()}`
  );
  return response.data;
}

/**
 * Create a movement template
 */
export async function createTemplate(
  request: CreateTemplateRequest
): Promise<CreateTemplateResponse> {
  const response = await apiClient.post<CreateTemplateResponse>(
    '/phases/templates',
    request
  );
  return response.data;
}

/**
 * Transform backend template response to frontend format
 */
function transformTemplateResponse(apiTemplate: RawTemplateResponse): MovementTemplate {
  return {
    ...apiTemplate,
    id: apiTemplate.template_id || apiTemplate.id || '',
    name: apiTemplate.video_name || apiTemplate.name || 'Unnamed Template',
    movement_type: apiTemplate.movement_name || apiTemplate.movement_type || 'Unknown',
  } as MovementTemplate;
}

/**
 * Get all movement templates
 */
export async function getTemplates(): Promise<MovementTemplate[]> {
  const response = await apiClient.get<RawTemplateResponse[]>('/phases/templates');
  return response.data.map(transformTemplateResponse);
}

/**
 * Get a single template by ID
 */
export async function getTemplate(templateId: string): Promise<MovementTemplate> {
  const response = await apiClient.get<RawTemplateResponse>(
    `/phases/templates/${templateId}`
  );
  return transformTemplateResponse(response.data);
}

/**
 * Delete a template
 */
export async function deleteTemplate(templateId: string): Promise<void> {
  await apiClient.delete(`/phases/templates/${templateId}`);
}

/**
 * Update a template
 */
export async function updateTemplate(
  templateId: string,
  request: Partial<CreateTemplateRequest>
): Promise<MovementTemplate> {
  const response = await apiClient.put<RawTemplateResponse>(
    `/phases/templates/${templateId}`,
    request
  );
  return transformTemplateResponse(response.data);
}

/**
 * Validate a template before finalizing.
 * Returns a structured report describing errors, warnings, and quality checks.
 */
export interface TemplateValidationResult {
  template_id: string;
  template_name: string;
  can_finalize: boolean;
  structural_errors: string[];
  validation_report: {
    overall_score: number;
    is_valid: boolean;
    phases: Array<{
      phase_name: string;
      form_score: number;
      goals: Array<{
        goal_type: string;
        goal_label: string;
        score: number;
        passed: boolean;
        issue: string | null;
      }>;
      issues: string[];
    }>;
    warnings: string[];
  } | null;
  quality_report: {
    overall_quality: string;
    score: number;
    checks: Array<{
      name: string;
      label: string;
      status: string;
      message: string;
      tip: string | null;
    }>;
  } | null;
  warnings: string[];
}

export async function validateTemplate(templateId: string): Promise<TemplateValidationResult> {
  const response = await apiClient.post<TemplateValidationResult>(
    `/templates/${templateId}/validate`
  );
  return response.data;
}

/**
 * Shape of `TemplateFinalizeResponse.auto_provisioned_stance_gate`
 * (backend/app/schemas/common.py).
 *
 * RETIRED SURFACE, TYPE KEPT (PR-5, owner decision D12). Finalize used to
 * auto-materialise a body-facing hard limit with a limit nobody typed; that
 * path is deleted on the backend, so this field is expected to stay null and
 * NO component may render it — a hard limit's failure angle comes from the
 * coach or from their own clips (REFACTOR_PLAN.md §C.3c). The type and the
 * mapping below stay so the two halves can ship independently without a wire
 * break. Historical context:
 *
 * It was set when finalize auto-materialized a
 * Stance Orientation axis_goal gate into the template's annotations
 * (automatic-orientation-epic Phase 1, backend shipped 2026-08-07) — see
 * `backend/app/api/template_routes.py` `finalize_template()`, which builds
 * this exact dict shape (`annotation_id`, `detected_lead_side`,
 * `detection_confidence`) right before setting it on the response.
 *
 * `detected_lead_side` is typed as the narrower 'left' | 'right' union
 * (matching the retired SuggestedStanceAnnotation._detected_lead_side):
 * the suggestion guard in `template_validation._suggest_stance_annotations`
 * bails out (returns no suggestion, so this field is never populated)
 * whenever detection is 'unknown'. `detection_confidence` is likewise
 * always a float on this path, but kept nullable here to match the
 * backend's `Optional[Dict[str, Any]]` typing exactly rather than assuming.
 */
export interface AutoProvisionedStanceGate {
  annotation_id: string;
  detected_lead_side: 'left' | 'right';
  detection_confidence: number | null;
}

/**
 * Finalize a template
 */
export interface FinalizeTemplateResult {
  template: MovementTemplate;
  /**
   * Non-null when finalize auto-materialized a Stance Orientation gate for
   * this template (automatic-orientation-epic Phase 1). null on the common
   * path — most finalizes don't provision (symmetric templates, a lead side
   * already declared, or detection confidence below threshold).
   *
   * Replaces the retired `suggestedAnnotations` field: the old "suggest and
   * wait for an Accept click" flow always resolved to an empty array once
   * finalize started materializing the gate directly, so no UI ever
   * consumed it (verified before removal) and it was deleted outright
   * rather than kept as permanently-dead plumbing. See
   * backlog/automatic-orientation-epic.md, Phase 1 REMAINDER.
   */
  autoProvisionedStanceGate: AutoProvisionedStanceGate | null;
}

export async function finalizeTemplate(
  templateId: string,
  finalizedBy?: string,
  skipScoringValidation?: boolean
): Promise<FinalizeTemplateResult> {
  const searchParams = new URLSearchParams();
  if (finalizedBy) searchParams.set('finalized_by', finalizedBy);
  if (skipScoringValidation) searchParams.set('skip_scoring_validation', 'true');
  const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const response = await apiClient.post<RawTemplateResponse>(
    `/templates/${templateId}/finalize${query}`
  );
  return {
    template: transformTemplateResponse(response.data),
    autoProvisionedStanceGate: response.data.auto_provisioned_stance_gate ?? null,
  };
}

/**
 * Update template status (finalized or draft)
 */
export async function updateTemplateStatus(
  templateId: string,
  status: 'draft' | 'finalized'
): Promise<MovementTemplate> {
  const response = await apiClient.patch<RawTemplateResponse>(
    `/phases/templates/${templateId}/status?status=${status}`
  );
  return transformTemplateResponse(response.data);
}
